<template>
  <div>
    <Head title="Online & Retail Orders" />
    <h1 class="mb-8 text-3xl font-bold">Orders</h1>
    <div class="flex items-center justify-between mb-6">
      <search-with-dropdown-filter v-model="search.search" class="mr-4 w-full max-w-md">
        <label class="block text-gray-700">By Dates:</label>
        <select v-model="search.deliveryDate" class="form-select mt-1 w-full">
          <option value="">-Select date-</option>
          <option value="delivery_date">Delivery Date</option>
          <option value="order_date">Order Date</option>
        </select>
      </search-with-dropdown-filter>
      <date-range v-model="search.dateValue" class="mr-0 w-full max-w-md" @reset="reset" />
      <button class="btn-orange text-white-400 hover:text-white-600" @click="searchOrders">Search</button>
      <Link class="btn-orange text-white-400 hover:text-white-600" href="/online-orders/create">Create</Link>
    </div>
    <div v-if="orders.data && url()" class="bg-white rounded-md shadow overflow-x-auto p-5 mb-5">
      <div class="flex items-center justify-between">
        <a
          class="btn-orange px-3 py-2 text-xs cursor-pointer"
          :href="`/download-online-orders${url()}`" 
          download
          target="__blank"
          title="Download"
        >
          <span>Download</span>
        </a>
      </div>
    </div>
    <hr />
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Order Date</th>
          <th class="pb-3 pt-3 px-3">Order Number</th>
          <th class="pb-3 pt-3 px-3">Delivery Date</th>
          <th class="pb-3 pt-3 px-3">Shipping Type</th>
          <th class="pb-3 pt-3 px-3">Customer</th>
          <th class="pb-3 pt-3 px-3">Contact</th> 
          <th class="pb-3 pt-3 px-3">Alternative Contact</th> 
          <th class="pb-3 pt-3 px-3">Email Address</th> 
          <th class="pb-3 pt-3 px-3 hidden">Physical Address</th> 
          <th class="pb-3 pt-3 px-3 hidden">City</th>
          <th class="pb-3 pt-3 px-3">Region</th>
          <th class="pb-3 pt-3 px-3 items-end justify-end hidden">Voucher Amount</th>
          <th class="pb-3 pt-3 px-3 items-end justify-end hidden">Transport</th>
          <th class="pb-3 pt-3 px-3 items-end justify-end">Total Amount</th>
          <th class="pb-3 pt-3 px-3">Agent</th>
          <th class="pb-3 pt-3 px-3">Status</th>
          <th class="pb-3 pt-3 px-3">Order Type</th>
          <th class="pb-3 pt-3 px-3">Freshchat User ID</th>
          <th class="pb-3 pt-3 px-3">Freshchat Conversation ID</th>
          <th class="pb-3 pt-3 px-3" />
        </tr>
        <tr v-for="order in orders.data" :key="order.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <!-- order.payments.length > 0 &&  -->
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              <svg v-show="order.payment_details === 'Prepaid'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6" style="display: inline;">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ order.date }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.order_no }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-2 text-center" tabindex="-1" :href="`/online-orders/${order.id}/show`">
              {{ order.delivery_date }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.shipping_type }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.customer }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.contact }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.alternative_contact }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.email_address }}
            </Link>
          </td>
          <td class="border-t hidden">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.physical_address }}
            </Link>
          </td>
          <td class="border-t hidden">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.city }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.region }}
            </Link>
          </td>
          <td class="border-t hidden">
            <Link v-if="order.voucherInfo && order.voucherInfo.code && order.has_discount_voucher == 0" class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.voucherInfo.voucher_amount }}
            </Link>
            <Link v-else class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              0.00
            </Link>
          </td>
          <td class="border-t hidden">
            <a class="flex items-end justify-end px-3 py-3 focus:text-orange-500" href="#" @click="openDetailPrompt(order)">
              {{ order.transport_amount }}
            </a>
          </td>
          <td class="border-t">
            <Link v-if="order.voucherInfo && order.voucherInfo.code" class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.voucherInfo.gross_amount }}
            </Link>
            <Link v-else class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.amount }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.agent }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.status }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/online-orders/${order.id}/show`">
              {{ order.order_type }}
            </Link>
          </td>
          <td class="border-t">
            {{ order.freshchat_user_id }}
          </td>
          <td class="border-t">
            <a
              v-if="order.freshchat_conversation_id"
              class="text-orange-600 hover:text-orange-500"
              target="_blank"
              :href="`${order.freshchat_messaging_url}`"
            >
              {{ order.freshchat_conversation_id }}
            </a>
            <a v-else class="flex items-center px-3 py-3 focus:text-orange-500" href="#">
              {{ order.freshchat_conversation_id }}
            </a>
          </td>
          <td class="w-px border-t">
          </td>
        </tr>
        <tr v-if="orders.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="9">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="orders.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchWithDropdownFilter from '@/Shared/SearchWithDropdownFilter'
import Pagination from '@/Shared/Pagination'
import DateRange from '@/Shared/DateRange'

export default {
  components: {
    Head,
    Icon,
    Link,
    SearchWithDropdownFilter,
    Pagination,
    DateRange,
  },
  layout: Layout,
  props: {
    filters: Object,
    orders: Object,
    errors: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        order_id: null,
      }),
      search: {
        search: this.filters.search,
        deliveryDate: this.filters.deliveryDate,
        dateValue: this.filters.dateValue,
      },
    }
  },
  computed: {
    user() {
      return this.$inertia.page.props.auth.user
    },
  },
  // watch: {
  //   form: {
  //     deep: true,
  //     handler: throttle(function () {
  //       this.$inertia.get('/online-orders', pickBy(this.search), { preserveState: true })
  //     }, 150),
  //   },
  // },
  methods: {
    reset() {
      this.search = mapValues(this.search, () => null)
    },
    searchOrders() {
      this.$inertia.get('/online-orders', pickBy(this.search), { preserveState: true })
    },
    url() {
      let query = ''
      if (this.search.search) {
        query += `search=${this.search.search}`
      }
      if (this.search.dateValue) {
        if (query) {
          query += '&'
        }
        query += `dateValue=${this.search.dateValue}`
      }
      if (this.search.deliveryDate) {
        if (query) {
          query += '&'
        }
        query += `deliveryDate=${this.search.deliveryDate}`
      }
      if (query) {
        query = `?${query}`
      }
      return query
    },
  },
}
</script>
<style type="text/css">
  .approved {
    color: green;
  }
  .declined {
    color: red;
  }
</style>
